import React, { ChangeEvent, useState } from 'react';
import { FormField } from './FormField';
import './PasswordField.scss';

interface PasswordFieldProps {
    id: string;
    label?: string;
    value: string | null | undefined;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    maxLength?: number;
    placeholder?: string;
    required?: boolean;
    tooltip?: string;
    error?: string;
    className?: string;
    canToggleVisibility?: boolean;
    autoComplete?: string;
    name?: string;
    dataLpIgnore?: boolean;
    ariaAutoComplete?: string;
    inputMode?: string;
    list?: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    /** When true, renders without FormField wrapper (for standalone use). */
    standalone?: boolean;
}

export const PasswordField: React.FC<PasswordFieldProps> = ({
    id,
    label = '',
    value,
    onChange,
    disabled = false,
    maxLength,
    placeholder,
    required = false,
    tooltip,
    error,
    className = '',
    canToggleVisibility = true,
    autoComplete,
    name,
    dataLpIgnore = false,
    ariaAutoComplete,
    inputMode,
    list,
    onKeyDown,
    standalone = false,
}) => {
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const inputElement = (
        <div className={`password-field-wrapper ${canToggleVisibility ? 'has-icon' : ''}`}>
            <input
                type={showPassword ? 'text' : 'password'}
                id={id}
                className={`form-control ${className}`}
                maxLength={maxLength}
                placeholder={placeholder}
                required={required}
                onChange={onChange}
                value={value || ''}
                disabled={disabled}
                autoComplete={autoComplete}
                name={name}
                data-lpignore={dataLpIgnore}
                aria-autocomplete={ariaAutoComplete as any}
                inputMode={inputMode as any}
                list={list}
                onKeyDown={onKeyDown}
            />
            {canToggleVisibility && (
                <i
                    title={showPassword ? 'Hide password' : 'Show password'}
                    className={`fa-solid password-field-icon ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                    onClick={togglePasswordVisibility}
                    role="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                />
            )}
        </div>
    );

    if (standalone) {
        return error ? (
            <>
                {inputElement}
                {error && <small className="text-danger">{error}</small>}
            </>
        ) : (
            inputElement
        );
    }

    return (
        <FormField
            label={label}
            id={id}
            required={required}
            tooltip={tooltip}
            error={error}
        >
            {inputElement}
        </FormField>
    );
};
